import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
} from "react";
import type { AlbumManifest, ManifestPage } from "../data/manifest";
import { PageRenderer } from "../render/PageRenderer";
import { CoverArt, coverBackHex } from "./CoverArt";
import { Colophon } from "./Colophon";
import { ContributePopover } from "./ContributePopover";
import { usePrefersReducedMotion } from "./useMediaQuery";
import { startCheckout } from "../data/checkout";
import { inviteCopy, pagesLabel } from "../data/invite";
import "./DesktopReader.css";

/**
 * Desktop reader (PRD §3 desktop): closed-album landing -> two-page spread with
 * a 3D page-flip. Turn via prev/next, arrow keys, click page-half, or drag
 * (follows cursor, completes past 45%). 76px bar + spread-thumbnail drawer +
 * end colophon. Respects prefers-reduced-motion (skip open; turns cross-fade).
 *
 * Pairing: interior pages are shown two-up by array position. spread s shows
 * [pages[2s], pages[2s+1]]. A forward turn animates a leaf whose FRONT is the
 * current right page and BACK is the next spread's left page, revealing the
 * next right page underneath — mirroring a real leaf turning on the spine.
 */

type Phase = "closed" | "opening" | "reading" | "end";

interface Flip {
  dir: "next" | "prev";
  frontIdx: number; // page index on the leaf's up-face at progress 0
  backIdx: number; // page index on the leaf's down-face (lands at progress 1)
  leftStatic: number; // page shown in the left slot during the turn
  rightStatic: number; // page shown in the right slot during the turn
  progress: number; // 0..1
  animating: boolean; // CSS transition active (vs. finger-following)
}

const DRAG_COMPLETE = 0.45;

export function DesktopReader({
  manifest,
  onCoverChange,
}: {
  manifest: AlbumManifest;
  onCoverChange?: (atCover: boolean) => void;
}) {
  const reduced = usePrefersReducedMotion();
  const pages = manifest.pages;
  // Book model: the front cover opens onto [inside-cover | page 1], so the
  // first content page is a recto (right). Boards = [INSIDE, p0, p1, …].
  const INSIDE = -1; // sentinel left board of spread 0 (inside front cover)
  const spreadCount = Math.max(1, Math.ceil((pages.length + 1) / 2));

  // Dev hooks (screenshot validation): ?open starts in the spread; ?flip=0.5
  // renders a static mid-turn. No effect in normal use.
  const devParams = new URLSearchParams(window.location.search);
  const devOpen = devParams.has("open");
  const devFlip = devParams.has("flip") ? Number(devParams.get("flip")) : null;

  const [phase, setPhase] = useState<Phase>(
    devOpen ? "reading" : devParams.has("coveropen") ? "opening" : "closed",
  );
  const [spread, setSpread] = useState(devOpen ? 1 : 0);
  const [flip, setFlip] = useState<Flip | null>(
    devFlip != null
      ? {
          dir: "next",
          frontIdx: 3,
          backIdx: 4,
          leftStatic: 2,
          rightStatic: 5,
          progress: devFlip,
          animating: false,
        }
      : null,
  );
  const [contribOpen, setContribOpen] = useState(devParams.has("contrib"));
  const [drawerOpen, setDrawerOpen] = useState(false);
  const bookRef = useRef<HTMLDivElement>(null);

  const leftIdx = spread * 2 - 1; // spread 0 => INSIDE
  const rightIdx = spread * 2; //    spread 0 => page 0 (page 1)

  // Report "on the cover" (haven't started reading) up to the live-update overlay.
  useEffect(() => {
    onCoverChange?.(phase === "closed" || phase === "opening");
  }, [phase, onCoverChange]);

  // --- image pre-decode (neighbours) ---
  const decodePage = useCallback(
    (idx: number) => {
      const page = pages[idx];
      if (!page) return;
      for (const slot of page.slots) {
        const photo = manifest.photos[slot.photoId];
        if (photo) {
          const img = new Image();
          img.src = photo.srcset.page;
          void img.decode?.().catch(() => {});
        }
      }
    },
    [pages, manifest.photos],
  );
  useEffect(() => {
    // Pre-decode the next + previous spreads before they are turned to.
    [leftIdx + 2, rightIdx + 2, leftIdx - 2, rightIdx - 2].forEach(decodePage);
  }, [leftIdx, rightIdx, decodePage]);

  // --- turn orchestration ---
  const canNext = spread < spreadCount - 1;
  const canPrev = spread > 0;

  const commit = useCallback((dir: "next" | "prev") => {
    setFlip(null);
    setSpread((s) => (dir === "next" ? s + 1 : s - 1));
  }, []);

  const startTurn = useCallback(
    (dir: "next" | "prev") => {
      if (flip) return;
      if (dir === "next" && !canNext) {
        setPhase("end"); // past the last spread -> colophon
        return;
      }
      if (dir === "prev" && !canPrev) return;

      if (reduced) {
        // Cross-fade instead of a 3D turn.
        commit(dir);
        return;
      }

      const base: Flip =
        dir === "next"
          ? {
              dir,
              frontIdx: rightIdx,
              backIdx: rightIdx + 1,
              leftStatic: leftIdx,
              rightStatic: rightIdx + 2,
              progress: 0,
              animating: true,
            }
          : {
              dir,
              frontIdx: leftIdx,
              backIdx: leftIdx - 1,
              leftStatic: leftIdx - 2,
              rightStatic: rightIdx,
              progress: 0,
              animating: true,
            };
      setFlip(base);
      // next frame -> animate to progress 1
      requestAnimationFrame(() =>
        requestAnimationFrame(() =>
          setFlip((f) => (f ? { ...f, progress: 1 } : f)),
        ),
      );
    },
    [flip, canNext, canPrev, reduced, rightIdx, leftIdx, commit],
  );

  // Keyboard navigation
  useEffect(() => {
    if (phase !== "reading") return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowRight" || e.key === " ") {
        e.preventDefault();
        startTurn("next");
      } else if (e.key === "ArrowLeft") {
        e.preventDefault();
        startTurn("prev");
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [phase, startTurn]);

  // --- drag to turn ---
  const drag = useRef<{ dir: "next" | "prev"; startX: number; moved: boolean } | null>(
    null,
  );
  const onPointerDown = (dir: "next" | "prev") => (e: React.PointerEvent) => {
    if (flip || reduced) return;
    // prev is bounded (can't turn before the first spread); next is always
    // allowed so a tap on the last spread can fall through to the colophon.
    if (dir === "prev" && !canPrev) return;
    drag.current = { dir, startX: e.clientX, moved: false };
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  };
  const onPointerMove = (e: React.PointerEvent) => {
    const d = drag.current;
    if (!d) return;
    const dx = e.clientX - d.startX;
    // Ignore sub-threshold jitter so a tap never spawns a stray flip.
    if (Math.abs(dx) <= 6 && !d.moved) return;
    d.moved = true;
    // No next page to turn to (last spread) -> the drag does nothing visually;
    // release still routes a tap to the colophon.
    if (d.dir === "next" && !canNext) return;
    const width = bookRef.current
      ? bookRef.current.clientWidth / 2
      : window.innerWidth / 2;
    const raw = d.dir === "next" ? -dx : dx; // drag inward
    const progress = Math.max(0, Math.min(1, raw / width));
    setFlip((f) => {
      const base: Flip =
        f ??
        (d.dir === "next"
          ? {
              dir: "next",
              frontIdx: rightIdx,
              backIdx: rightIdx + 1,
              leftStatic: leftIdx,
              rightStatic: rightIdx + 2,
              progress: 0,
              animating: false,
            }
          : {
              dir: "prev",
              frontIdx: leftIdx,
              backIdx: leftIdx - 1,
              leftStatic: leftIdx - 2,
              rightStatic: rightIdx,
              progress: 0,
              animating: false,
            });
      return { ...base, progress, animating: false };
    });
  };
  const onPointerUp = (dir: "next" | "prev") => (e: React.PointerEvent) => {
    const d = drag.current;
    drag.current = null;
    if (!d) return;
    (e.currentTarget as HTMLElement).releasePointerCapture?.(e.pointerId);
    // A tap (no real movement) = click that page-half to turn.
    if (!d.moved) {
      startTurn(dir);
      return;
    }
    setFlip((f) => {
      if (!f) return f;
      const complete = f.progress > DRAG_COMPLETE;
      return { ...f, progress: complete ? 1 : 0, animating: true };
    });
  };

  // Commit / cancel when the leaf transition ends. Guard to the leaf's OWN
  // transform transition — a bubbled child transition or a non-transform
  // property would otherwise commit mid-flight and cause a visible snap.
  const onLeafTransitionEnd = (e: React.TransitionEvent) => {
    if (e.target !== e.currentTarget || e.propertyName !== "transform") return;
    setFlip((f) => {
      if (!f || !f.animating) return f;
      if (f.progress >= 1) {
        setSpread((s) => (f.dir === "next" ? s + 1 : s - 1));
        return null;
      }
      return null; // cancelled back to 0
    });
  };

  // "Get it printed" → Stripe hosted Checkout (Anvil slice-2 contract).
  const [checkoutBusy, setCheckoutBusy] = useState(false);
  const onGetPrinted = useCallback(() => {
    if (!manifest.print.available || checkoutBusy) return;
    setCheckoutBusy(true);
    // Redirects on success; only returns here on error.
    startCheckout(manifest.id).catch(() => setCheckoutBusy(false));
  }, [manifest.print.available, manifest.id, checkoutBusy]);

  // --- render helpers ---
  const coverBack = coverBackHex(manifest.cover);
  const face = (idx: number, priority = false) => {
    if (idx === INSIDE)
      return <div className="pb-page pb-inside" style={{ background: coverBack }} />;
    const page: ManifestPage | undefined = pages[idx];
    if (!page) return <div className="pb-page" style={{ background: "var(--page)" }} />;
    return <PageRenderer page={page} photos={manifest.photos} priority={priority} />;
  };

  const openAlbum = () => {
    if (phase !== "closed") return;
    if (reduced) {
      setPhase("reading");
      return;
    }
    setPhase("opening");
  };
  // Cover finished swinging (rotateY -180) -> land on the first spread. The
  // cover's back face is already coverBack on the left board and page 1 is
  // already the right board, so the switch is dead-still (no reparent pop).
  const onCoverTransitionEnd = (e: React.TransitionEvent) => {
    if (phase !== "opening") return;
    if (e.target === e.currentTarget && e.propertyName === "transform") {
      setPhase("reading");
    }
  };

  // ---- CLOSED / OPENING landing (cover opens like a book) ----
  if (phase === "closed" || phase === "opening") {
    const opening = phase === "opening";
    const invite = inviteCopy(manifest.sender.name, "desktop");
    return (
      <div className="d-stage">
        <div className="d-book d-book--closed">
          {/* Left board: the collaborate-invite caption (cover swings onto it). */}
          <div className="d-landing-meta">
            <h2 className="d-invite-head">{invite.headline}</h2>
            <p className="d-invite-sub">{invite.sub}</p>
            <p className="d-landing-hint">
              Tap the cover to open · {pagesLabel(manifest.pageCount)}
            </p>
          </div>
          {/* Right board: page 1 sits underneath, revealed as the cover lifts. */}
          <div className="d-slot d-slot--right">{face(0, true)}</div>
          {/* Spine cast-shadow on page 1, recedes as the cover flattens. */}
          <div
            className={`d-open-spine ${opening ? "is-opening" : ""}`}
            aria-hidden="true"
          />
          {/* The swinging front cover, hinged at the spine (centerline). */}
          <button
            type="button"
            className={`d-cover ${opening ? "is-opening" : ""}`}
            onClick={openAlbum}
            aria-label="Open the album"
            onTransitionEnd={onCoverTransitionEnd}
          >
            <div className="d-cover__face d-cover__front">
              <CoverArt manifest={manifest} />
            </div>
            <div
              className="d-cover__face d-cover__back"
              style={{ background: coverBack }}
            />
            <div className="d-cover__shade" aria-hidden="true" />
          </button>
        </div>
      </div>
    );
  }

  // ---- END colophon ----
  if (phase === "end") {
    return (
      <div className="d-stage d-end">
        <Colophon manifest={manifest} onContribute={() => setContribOpen(true)} />
        <button
          type="button"
          className="btn btn--ghost d-back"
          onClick={() => setPhase("reading")}
        >
          Back to the album
        </button>
        <ContributePopover
          open={contribOpen}
          joinCode={manifest.joinCode}
          onClose={() => setContribOpen(false)}
        />
      </div>
    );
  }

  // ---- READING spread ----
  const flipAngle = flip
    ? flip.dir === "next"
      ? flip.progress * -180
      : flip.progress * 180
    : 0;

  // Turning readout (Iris §transient): during a flip the readout shows the
  // destination pages, dimmed, then settles back.
  const total = manifest.pageCount;
  const turningLeft = flip
    ? flip.dir === "next"
      ? leftIdx + 2
      : leftIdx - 2
    : null;
  const turningLabel =
    turningLeft != null
      ? `${Math.max(1, turningLeft + 1)}–${Math.min(turningLeft + 2, total)}`
      : null;

  return (
    <div className="d-stage">
      <div
        className="d-book"
        ref={bookRef}
        role="group"
        aria-live="polite"
        // Under reduced motion, remount per spread so the CSS cross-fade runs
        // (PRD §3: turns become 160ms cross-fades, not a 3D rotation).
        key={reduced ? spread : undefined}
      >
        <span className="d-sr-only">
          {leftIdx < 0
            ? `Page ${rightIdx + 1}`
            : `Pages ${leftIdx + 1} and ${rightIdx + 1}`}{" "}
          of {manifest.pageCount}
        </span>

        {/* Left page slot */}
        <div className="d-slot d-slot--left">
          {face(flip ? flip.leftStatic : leftIdx, true)}
        </div>
        {/* Right page slot */}
        <div className="d-slot d-slot--right">
          {face(flip ? flip.rightStatic : rightIdx, true)}
        </div>

        {/* Spine gradient over the gutter */}
        <div className="d-spine" aria-hidden="true" />

        {/* Turning leaf */}
        {flip && (
          <div
            className={`d-leaf d-leaf--${flip.dir} ${
              flip.animating ? "is-animating" : ""
            }`}
            style={{ transform: `rotateY(${flipAngle}deg)` } as CSSProperties}
            onTransitionEnd={onLeafTransitionEnd}
          >
            <div className="d-leaf__face d-leaf__front">{face(flip.frontIdx)}</div>
            <div className="d-leaf__face d-leaf__back">{face(flip.backIdx)}</div>
          </div>
        )}

        {/* Click / drag hit zones (halves) */}
        {!flip?.animating && (
          <>
            <div
              className={`d-hit d-hit--left ${canPrev ? "" : "is-disabled"}`}
              onPointerDown={onPointerDown("prev")}
              onPointerMove={onPointerMove}
              onPointerUp={onPointerUp("prev")}
              aria-hidden="true"
            />
            <div
              className="d-hit d-hit--right"
              onPointerDown={onPointerDown("next")}
              onPointerMove={onPointerMove}
              onPointerUp={onPointerUp("next")}
              aria-hidden="true"
            />
          </>
        )}
      </div>

      <DesktopBar
        manifest={manifest}
        leftIdx={leftIdx}
        rightIdx={rightIdx}
        canPrev={canPrev}
        turningLabel={turningLabel}
        onPrev={() => startTurn("prev")}
        onNext={() => startTurn("next")}
        drawerOpen={drawerOpen}
        toggleDrawer={() => setDrawerOpen((v) => !v)}
        onGetPrinted={onGetPrinted}
        checkoutBusy={checkoutBusy}
        onContribute={() => setContribOpen(true)}
      />

      {drawerOpen && (
        <ThumbnailDrawer
          manifest={manifest}
          current={spread}
          onPick={(s) => {
            setFlip(null);
            setSpread(s);
            setDrawerOpen(false);
          }}
        />
      )}

      <ContributePopover
        open={contribOpen}
        joinCode={manifest.joinCode}
        onClose={() => setContribOpen(false)}
      />
    </div>
  );
}

// --- chrome bar (Iris footer spec: NAV · JOIN CODE · ACTIONS) ---
function DesktopBar({
  manifest,
  leftIdx,
  rightIdx,
  canPrev,
  turningLabel,
  onPrev,
  onNext,
  drawerOpen,
  toggleDrawer,
  onGetPrinted,
  checkoutBusy,
  onContribute,
}: {
  manifest: AlbumManifest;
  leftIdx: number;
  rightIdx: number;
  canPrev: boolean;
  turningLabel: string | null;
  onPrev: () => void;
  onNext: () => void;
  drawerOpen: boolean;
  toggleDrawer: () => void;
  onGetPrinted: () => void;
  checkoutBusy: boolean;
  onContribute: () => void;
}) {
  const total = manifest.pageCount;
  const spreadLabel =
    leftIdx < 0
      ? `${Math.min(rightIdx + 1, total)} / ${total}` // inside-cover | page 1
      : `${Math.min(leftIdx + 1, total)}–${Math.min(rightIdx + 1, total)} / ${total}`;
  const printable = manifest.print.available;
  return (
    <div className="d-bar" role="toolbar" aria-label="Reader controls">
      {/* Zone A — nav */}
      <div className="d-zone d-zone--nav">
        <button
          type="button"
          className="d-nav"
          onClick={onPrev}
          disabled={!canPrev}
          aria-label="Previous spread"
        >
          ‹
        </button>
        <button
          type="button"
          className="d-nav"
          onClick={onNext}
          aria-label="Next spread"
        >
          ›
        </button>
        <div className="d-readout" aria-live="polite">
          <span className={`d-readout__main ${turningLabel ? "is-turning" : ""}`}>
            {turningLabel ?? spreadLabel}
          </span>
          <span className="d-readout__cap">{turningLabel ? "Turning" : "Spreads"}</span>
        </div>
        <button
          type="button"
          className={`d-thumbs-toggle ${drawerOpen ? "is-open" : ""}`}
          onClick={toggleDrawer}
          aria-expanded={drawerOpen}
          aria-label="All spreads"
        >
          ⊞
        </button>
      </div>

      {/* Center spacer — join code moved into the contribute popover (the code
          is the album's access key, so it must not sit in always-on chrome). */}
      <div className="d-zone d-zone--code" aria-hidden="true" />

      {/* Zone C — actions (contribute leads: lavender primary sits rightmost) */}
      <div className="d-zone d-zone--actions">
        <button
          type="button"
          className="d-cta d-cta--ink"
          disabled={!printable || checkoutBusy}
          onClick={onGetPrinted}
        >
          {checkoutBusy ? "Preparing…" : "Get it printed"}
        </button>
        <button
          type="button"
          className="d-cta d-cta--primary"
          onClick={onContribute}
        >
          Add your photos
        </button>
      </div>
    </div>
  );
}

// --- spread thumbnail drawer (expands to 250px) ---
function ThumbnailDrawer({
  manifest,
  current,
  onPick,
}: {
  manifest: AlbumManifest;
  current: number;
  onPick: (spread: number) => void;
}) {
  // Offset book model: spread s = [pages[2s-1], pages[2s]]; spread 0's left is
  // the inside cover (INSIDE = -1) rendered as the coverBack solid.
  const spreadCount = Math.max(1, Math.ceil((manifest.pages.length + 1) / 2));
  const coverBack = coverBackHex(manifest.cover);
  const mini = (idx: number) => {
    if (idx < 0)
      return <div className="pb-page" style={{ background: coverBack }} />;
    const p = manifest.pages[idx];
    return p ? <PageRenderer page={p} photos={manifest.photos} /> : <div className="pb-page" />;
  };
  return (
    <div className="d-drawer" role="dialog" aria-label="All spreads">
      <div className="d-drawer__scroll">
        {Array.from({ length: spreadCount }, (_, s) => (
          <button
            type="button"
            key={s}
            className={`d-drawer__spread ${s === current ? "is-current" : ""}`}
            onClick={() => onPick(s)}
            aria-label={`Spread ${s + 1}`}
          >
            <div className="d-drawer__mini">{mini(s * 2 - 1)}</div>
            <div className="d-drawer__mini">{mini(s * 2)}</div>
          </button>
        ))}
      </div>
      <span className="d-drawer__cap">Tap a spread to jump</span>
    </div>
  );
}
